<script setup>
// NOTICE: KAPTION-INTEGRATION
// sales-pipeline.CARD.1 — one conversation on the Sales Pipeline board: avatar,
// name, time, 1-line preview, up to 2 non-stage labels (+n), inbox icon,
// waiting time (from waiting_since) and assignee avatar.

import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Avatar from 'next/avatar/Avatar.vue';
import {
  formatWaiting,
  nonStageLabels,
  waitingSeconds,
} from 'dashboard/helper/kaptionSalesPipeline.js';

const props = defineProps({
  conversation: { type: Object, required: true },
  stages: { type: Array, default: () => [] },
  stageColor: { type: String, default: '#94A3B8' },
  nowSeconds: { type: Number, default: () => Math.floor(Date.now() / 1000) },
  focused: { type: Boolean, default: false },
  // sales-pipeline.CARD.2 — condensed (~2 lines) follows the list's layout toggle.
  condensed: { type: Boolean, default: false },
});

const emit = defineEmits(['open']);

const { t, locale } = useI18n();
const store = useStore();

// Privacy mode (blur card content, hover to reveal) — same contract as the legacy card.
const kaptionPrivacyEnabled = inject('kaptionPrivacyEnabled', ref(false));
const hovering = ref(false);
const isBlurred = computed(
  () => kaptionPrivacyEnabled.value && !hovering.value
);

const sender = computed(() => props.conversation.meta?.sender || {});
const contactName = computed(
  () => sender.value.name || t('CONVERSATION.NO_CONTACT_NAME')
);
const assignee = computed(() => props.conversation.meta?.assignee || null);

const preview = computed(() => {
  const message = props.conversation.messages?.[0];
  const text =
    message?.content ||
    props.conversation.last_non_activity_message?.content ||
    '';
  return text
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
});

// sales-pipeline.CARD.3 — the conversation list's short form ("4h", "2d"), so the
// time never squeezes the name; the full relative time follows the user's
// locale in the tooltip. nowSeconds keeps both fresh.
const activityTs = computed(
  () => props.conversation.last_activity_at || props.conversation.timestamp
);
const timeAgo = computed(() => {
  // eslint-disable-next-line no-unused-expressions
  props.nowSeconds; // re-evaluate every minute
  if (!activityTs.value) return '';
  const short = shortTimestamp(dynamicTime(activityTs.value));
  return short === 'now'
    ? t('KAPTION_PLATFORM.SALES_PIPELINE.CARD.NOW')
    : short;
});
const timeTitle = computed(() =>
  activityTs.value ? dynamicTime(activityTs.value, locale.value) : ''
);

const labelsByTitle = computed(() => {
  const map = {};
  (store.getters['labels/getLabels'] || []).forEach(l => {
    map[l.title] = l;
  });
  return map;
});

const otherLabels = computed(() =>
  nonStageLabels(props.conversation, props.stages)
);
const shownLabels = computed(() =>
  otherLabels.value.slice(0, 2).map(title => ({
    title,
    color: labelsByTitle.value[title]?.color || '#94A3B8',
  }))
);
const hiddenLabelCount = computed(() =>
  Math.max(0, otherLabels.value.length - 2)
);

const inbox = computed(() =>
  store.getters['inboxes/getInbox']?.(props.conversation.inbox_id)
);
const inboxIcon = computed(() => {
  const type = inbox.value?.channel_type || props.conversation.meta?.channel;
  return type
    ? getInboxIconByType(type, inbox.value?.medium, 'line')
    : 'i-lucide-inbox';
});

const waiting = computed(() =>
  formatWaiting(waitingSeconds(props.conversation, props.nowSeconds))
);
const waitingLong = computed(
  () => waitingSeconds(props.conversation, props.nowSeconds) > 60 * 60
);
</script>

<template>
  <div
    class="pipeline-card group relative rounded-lg border border-n-weak bg-n-solid-1 cursor-pointer select-none transition-shadow hover:shadow-md hover:border-n-strong outline-none"
    :class="[
      condensed ? 'px-2 py-1.5' : 'px-2.5 py-2',
      { 'kaption-privacy-blur': isBlurred },
      focused ? 'ring-2 ring-n-teal-8' : '',
    ]"
    :data-conversation-id="conversation.id"
    :data-density="condensed ? 'condensed' : 'expanded'"
    data-testid="pipeline-card"
    @click="emit('open', conversation)"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <span
      class="absolute left-0 w-[3px] rounded-r"
      :class="condensed ? 'inset-y-1' : 'inset-y-1.5'"
      :style="{ backgroundColor: stageColor }"
    />
    <!-- Condensed: line 1 avatar · name · waiting · time; line 2 preview · labels · unread · assignee -->
    <div v-if="condensed" class="flex items-start gap-1.5 min-w-0">
      <Avatar
        :src="sender.thumbnail || ''"
        :name="contactName"
        :size="20"
        rounded-full
      />
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1 min-w-0 h-5">
          <span
            class="text-[13px] font-medium truncate text-n-slate-12 min-w-0"
          >
            {{ contactName }}
          </span>
          <span
            v-if="waiting"
            class="inline-flex items-center gap-px text-[10px] shrink-0"
            :class="waitingLong ? 'text-n-ruby-11' : 'text-n-amber-11'"
            :title="t('KAPTION_PLATFORM.SALES_PIPELINE.CARD.WAITING')"
            data-testid="pipeline-card-waiting"
          >
            <i class="i-lucide-hourglass size-2.5" />
            {{ waiting }}
          </span>
          <span
            class="ml-auto text-[10px] shrink-0 whitespace-nowrap text-n-slate-10"
            :title="timeTitle"
            data-testid="pipeline-card-time"
          >
            {{ timeAgo }}
          </span>
        </div>
        <div class="flex items-center gap-1 min-w-0 h-4">
          <span
            class="flex-1 min-w-0 text-xs leading-4 truncate text-n-slate-11"
          >
            {{ preview }}
          </span>
          <span
            v-for="label in shownLabels"
            :key="label.title"
            class="size-1.5 rounded-full shrink-0"
            :style="{ backgroundColor: label.color }"
            :title="label.title"
            data-testid="pipeline-card-label-dot"
          />
          <span
            v-if="conversation.unread_count > 0"
            class="h-3.5 min-w-[14px] px-1 flex items-center justify-center text-[9px] font-medium rounded-full bg-n-teal-9 text-white shrink-0"
          >
            {{
              conversation.unread_count > 9 ? '9+' : conversation.unread_count
            }}
          </span>
          <Avatar
            v-if="assignee"
            :src="assignee.thumbnail || ''"
            :name="assignee.name || ''"
            :size="16"
            rounded-full
          />
        </div>
      </div>
    </div>
    <div v-else class="flex items-start gap-2">
      <Avatar
        :src="sender.thumbnail || ''"
        :name="contactName"
        :size="24"
        rounded-full
      />
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between gap-1">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ contactName }}
          </span>
          <span
            class="text-[10px] shrink-0 whitespace-nowrap text-n-slate-10"
            :title="timeTitle"
            data-testid="pipeline-card-time"
          >
            {{ timeAgo }}
          </span>
        </div>
        <p
          v-if="preview"
          class="mt-0.5 mb-0 text-xs leading-4 truncate text-n-slate-11"
        >
          {{ preview }}
        </p>
        <div
          v-if="shownLabels.length"
          class="flex items-center gap-1 mt-1.5 min-w-0"
        >
          <span
            v-for="label in shownLabels"
            :key="label.title"
            class="inline-flex items-center gap-1 max-w-[7rem] px-1.5 py-px rounded-md bg-n-alpha-2 text-[10px] text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="truncate">{{ label.title }}</span>
          </span>
          <span
            v-if="hiddenLabelCount"
            class="text-[10px] text-n-slate-10"
            data-testid="pipeline-card-more-labels"
          >
            +{{ hiddenLabelCount }}
          </span>
        </div>
        <div class="flex items-center justify-between mt-1.5 gap-1">
          <div class="flex items-center gap-1.5 min-w-0 text-n-slate-10">
            <i :class="inboxIcon" class="size-3.5 shrink-0" />
            <span class="text-[10px] font-mono">#{{ conversation.id }}</span>
            <span
              v-if="waiting"
              class="inline-flex items-center gap-0.5 text-[10px]"
              :class="waitingLong ? 'text-n-ruby-11' : 'text-n-amber-11'"
              :title="t('KAPTION_PLATFORM.SALES_PIPELINE.CARD.WAITING')"
              data-testid="pipeline-card-waiting"
            >
              <i class="i-lucide-hourglass size-3" />
              {{ waiting }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <span
              v-if="conversation.unread_count > 0"
              class="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-medium rounded-full bg-n-teal-9 text-white"
            >
              {{
                conversation.unread_count > 9 ? '9+' : conversation.unread_count
              }}
            </span>
            <Avatar
              v-if="assignee"
              :src="assignee.thumbnail || ''"
              :name="assignee.name || ''"
              :size="18"
              rounded-full
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pipeline-card.sortable-ghost {
  opacity: 0.45;
}

.pipeline-card.sortable-drag {
  transform: rotate(1deg);
}

.kaption-privacy-blur {
  filter: blur(6px);
  user-select: none;
}
</style>
